import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiErrorMessage } from "@/api/client";
import type { PaginationMeta } from "./types";

const EXTRA_KEYS = [
  "severity",
  "priority",
  "market_id",
  "target_type",
  "target_id",
  "signal_type",
  "owner_user_id",
] as const;

export function useMarketplaceQueue<T>(
  loadPage: (params: {
    cursor?: string;
    status: string;
    sort: string;
    extra: Record<string, string>;
  }) => Promise<{ items: T[]; pagination: PaginationMeta }>,
  defaults?: { status?: string; sort?: string; extra?: Record<string, string> },
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status") ?? defaults?.status ?? "";
  const sort = searchParams.get("sort") ?? defaults?.sort ?? "";
  const cursor = searchParams.get("cursor") ?? undefined;
  const extra: Record<string, string> = { ...(defaults?.extra ?? {}) };
  for (const key of EXTRA_KEYS) {
    const value = searchParams.get(key);
    if (value) extra[key] = value;
  }

  const [rows, setRows] = useState<T[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const extraKey = JSON.stringify(extra);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await loadPage({
        ...(cursor ? { cursor } : {}),
        status,
        sort,
        extra: JSON.parse(extraKey) as Record<string, string>,
      });
      setRows(result.items);
      setNextCursor(result.pagination.next_cursor ?? null);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRows([]);
      setNextCursor(null);
    } finally {
      setLoading(false);
    }
  }, [loadPage, cursor, status, sort, extraKey]);

  useEffect(() => {
    void load();
  }, [load]);

  function updateParams(patch: Record<string, string | undefined>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined || value === "") next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next);
  }

  const page = cursorStack.length + 1;

  return {
    status,
    sort,
    extra,
    rows,
    loading,
    error,
    nextCursor,
    page,
    load,
    setStatus(value: string) {
      setCursorStack([]);
      updateParams({ status: value || undefined, cursor: undefined });
    },
    setSort(value: string) {
      setCursorStack([]);
      updateParams({ sort: value, cursor: undefined });
    },
    setExtra(key: string, value: string) {
      setCursorStack([]);
      updateParams({ [key]: value || undefined, cursor: undefined });
    },
    clearFilters() {
      setCursorStack([]);
      const next = new URLSearchParams();
      if (defaults?.extra) {
        for (const [key, value] of Object.entries(defaults.extra)) {
          next.set(key, value);
        }
      }
      setSearchParams(next);
    },
    goNext() {
      if (!nextCursor) return;
      setCursorStack((stack) => [...stack, cursor ?? ""]);
      updateParams({ cursor: nextCursor });
    },
    goPrevious() {
      if (cursorStack.length === 0) return;
      const stack = [...cursorStack];
      const previous = stack.pop();
      setCursorStack(stack);
      updateParams({
        cursor: previous && previous.length > 0 ? previous : undefined,
      });
    },
  };
}
